import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Server,
  ShieldCheck,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Send,
  Coins,
  ExternalLink,
  Inbox,
  Settings as SettingsIcon,
  Ban,
  Eye,
  MousePointerClick,
  MessageCircle,
  Workflow,
  LayoutDashboard,
  Activity as ActivityIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { openCheckout } from "@/lib/razorpay";
import OutreachInbox from "@/components/OutreachInbox";
import OutreachStats from "@/components/OutreachStats";
import OutreachActivity from "@/components/OutreachActivity";
import SequencesPanel from "@/components/SequencesPanel";

const PRESETS = [
  { key: "gmail", label: "Gmail", host: "smtp.gmail.com", port: 465, ssl: true, imap_host: "imap.gmail.com", imap_port: 993, hint: "Use an App Password (Google Account → Security → 2FA → App passwords).", help_url: "https://support.google.com/accounts/answer/185833", help_label: "How to create a Gmail App password" },
  { key: "outlook", label: "Outlook / Microsoft 365", host: "smtp.office365.com", port: 587, ssl: false, imap_host: "outlook.office365.com", imap_port: 993, hint: "Some tenants require admin to enable SMTP AUTH.", help_url: "https://support.microsoft.com/en-us/account-billing/how-to-get-and-use-app-passwords-5896ed9b-4263-e681-128a-a6f2979a7944", help_label: "How to get a Microsoft App password" },
  { key: "zoho", label: "Zoho Mail", host: "smtp.zoho.com", port: 465, ssl: true, imap_host: "imap.zoho.com", imap_port: 993, hint: "Generate an app password under Zoho Account → Security.", help_url: "https://www.zoho.com/mail/help/adminconsole/two-factor-authentication.html#alink7", help_label: "How to create a Zoho App password" },
  { key: "hostinger", label: "Hostinger", host: "smtp.hostinger.com", port: 465, ssl: true, imap_host: "imap.hostinger.com", imap_port: 993, hint: "Use your mailbox password directly.", help_url: "https://support.hostinger.com/en/articles/1583298-how-to-find-and-set-up-mail-credentials-in-hpanel", help_label: "How to find Hostinger mailbox password" },
  { key: "brevo", label: "Brevo (Sendinblue)", host: "smtp-relay.brevo.com", port: 587, ssl: false, imap_host: "", imap_port: 993, hint: "Login = your account email, password = SMTP key. Brevo doesn't expose IMAP.", help_url: "https://help.brevo.com/hc/en-us/articles/209462765", help_label: "How to get a Brevo SMTP key" },
];

// Detect provider from the form host so the help link works even after a manual host edit.
function detectPresetByHost(host) {
  if (!host) return null;
  const h = host.toLowerCase();
  if (h.includes("gmail") || h.endsWith("smtp.googlemail.com") || h.includes("google")) return PRESETS[0];
  if (h.includes("office365") || h.includes("outlook") || h.includes("hotmail") || h.includes("live.com")) return PRESETS[1];
  if (h.includes("zoho")) return PRESETS[2];
  if (h.includes("hostinger")) return PRESETS[3];
  if (h.includes("brevo") || h.includes("sendinblue")) return PRESETS[4];
  return null;
}

const EMPTY_FORM = {
  host: "",
  port: 465,
  username: "",
  password: "",
  use_ssl: true,
  from_name: "",
  from_address: "",
  imap_host: "",
  imap_port: 993,
  imap_use_ssl: true,
};

export default function OutreachSettings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard"); // dashboard | sequences | activity | inbox | setup | suppression
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [balance, setBalance] = useState({ purchased: 0, used: 0, balance: 0 });
  const [packs, setPacks] = useState([]);
  const [packBusy, setPackBusy] = useState(null);

  const loadAll = async () => {
    const [smtp, bal, p] = await Promise.all([
      http.get("/outreach/smtp").then((r) => r.data.config).catch(() => null),
      http.get("/outreach/credits/balance").then((r) => r.data).catch(() => null),
      http.get("/outreach/credits/packs").then((r) => r.data.packs).catch(() => []),
    ]);
    setConfig(smtp);
    if (bal) setBalance(bal);
    setPacks(p || []);
    if (smtp) {
      setForm({
        host: smtp.host || "",
        port: smtp.port || 465,
        username: smtp.username || "",
        password: "",
        use_ssl: smtp.use_ssl !== false,
        from_name: smtp.from_name || user?.name || "",
        from_address: smtp.from_address || smtp.username || "",
        imap_host: smtp.imap_host || "",
        imap_port: smtp.imap_port || 993,
        imap_use_ssl: smtp.imap_use_ssl !== false,
      });
    } else if (user) {
      setForm((f) => ({ ...f, from_name: user.name || "", from_address: user.email || "" }));
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (preset) => {
    setForm((f) => ({
      ...f,
      host: preset.host,
      port: preset.port,
      use_ssl: preset.ssl,
      imap_host: preset.imap_host || "",
      imap_port: preset.imap_port || 993,
      imap_use_ssl: true,
    }));
    toast.info(preset.hint);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.host || !form.username) {
      return toast.error("Host and username are required");
    }
    // On first save the password is required. When updating an existing
    // record we let the user leave it blank to keep the previously stored one.
    if (!config && !form.password) {
      return toast.error("Password / app password is required on first save");
    }
    setBusy(true);
    try {
      const payload = {
        host: form.host.trim(),
        port: Number(form.port),
        username: form.username.trim().toLowerCase(),
        use_ssl: !!form.use_ssl,
        from_name: form.from_name.trim() || undefined,
        from_address: (form.from_address || form.username).trim().toLowerCase(),
        imap_host: form.imap_host?.trim() || undefined,
        imap_port: form.imap_port ? Number(form.imap_port) : undefined,
        imap_use_ssl: !!form.imap_use_ssl,
      };
      if (form.password) payload.password = form.password;
      await http.post("/outreach/smtp", payload);
      toast.success("Saved. Click 'Test connection' to verify.");
      setForm((f) => ({ ...f, password: "" }));
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await http.post("/outreach/smtp/test");
      if (data.ok) {
        toast.success("Connection verified");
        await loadAll();
      } else {
        toast.error(data.error || "Connection failed");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const testImap = async () => {
    setTestingImap(true);
    try {
      const { data } = await http.post("/outreach/imap/test");
      if (data.ok) {
        toast.success("IMAP verified — replies will appear in Inbox tab");
        await loadAll();
      } else {
        toast.error(data.error || "IMAP connection failed");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "IMAP test failed");
    } finally {
      setTestingImap(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove SMTP credentials? You won't be able to send emails until you reconnect.")) return;
    try {
      await http.delete("/outreach/smtp");
      setConfig(null);
      setForm({ ...EMPTY_FORM, from_name: user?.name || "", from_address: user?.email || "" });
      toast.success("SMTP removed");
    } catch (e) {
      toast.error("Could not remove SMTP");
    }
  };

  const buyPack = async (pack) => {
    setPackBusy(pack.id);
    try {
      const { data } = await http.post("/outreach/credits/checkout", { pack_id: pack.id });
      if (data.demo) {
        toast.success(`+${pack.credits.toLocaleString()} credits added (demo)`);
        await loadAll();
        return;
      }
      if (data.affiliate && data.affiliate.applied) {
        toast.success(
          `${data.affiliate.partner_name} discount applied · ${data.affiliate.discount_pct}% off ($${data.affiliate.discount_amount_usd.toFixed(2)} saved)`,
          { duration: 6000 }
        );
      }
      if (data.tax && (data.tax.tax_amount_usd || 0) > 0) {
        toast.info(
          `Charging $${data.tax.total_usd.toFixed(2)} · $${data.tax.subtotal_usd.toFixed(2)} + ${data.tax.tax_label} $${data.tax.tax_amount_usd.toFixed(2)}`,
          { duration: 5000 }
        );
      }
      const rzp = data.razorpay;
      await openCheckout({
        key: rzp.key_id,
        amount: rzp.amount,
        currency: rzp.currency,
        order_id: rzp.order_id,
        name: rzp.name,
        description: rzp.description,
        prefill: rzp.prefill,
        theme: { color: "#0a0a0a" },
        modal: { ondismiss: () => setPackBusy(null) },
        handler: async (resp) => {
          try {
            await http.post("/outreach/credits/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`+${pack.credits.toLocaleString()} email credits added`);
            await loadAll();
          } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed");
          } finally {
            setPackBusy(null);
          }
        },
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start checkout");
      setPackBusy(null);
    }
  };

  // ----- Gmail one-click connect -----
  const isGmailOAuth = config?.provider === "gmail_oauth";

  const connectGmail = () => {
    // Full-page redirect to backend, which redirects to Google's consent
    // screen with the gmail.send scope. Uses the browser origin so preview
    // and production both work without any hardcoded URL.
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${backend}/api/outreach/gmail/connect`;
  };

  const disconnectGmail = async () => {
    if (!window.confirm("Disconnect Gmail? You won't be able to send outreach until you reconnect.")) return;
    try {
      await http.post("/outreach/gmail/disconnect");
      setConfig(null);
      toast.success("Gmail disconnected");
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not disconnect");
    }
  };

  // Handle ?gmail=connected / ?gmail_error=... redirect payloads once.
  useEffect(() => {
    const url = new URL(window.location.href);
    const ok = url.searchParams.get("gmail");
    const err = url.searchParams.get("gmail_error");
    let touched = false;
    if (ok === "connected") {
      toast.success("Gmail connected");
      url.searchParams.delete("gmail");
      touched = true;
      setTab("setup");
      loadAll();
    }
    if (err) {
      const map = {
        state_expired: "That Gmail connection window expired — please retry.",
        no_refresh_token: "Google didn't return a refresh token. Try again (or remove access at myaccount.google.com and retry).",
        missing_send_scope: "You need to tick the Gmail Send permission — please retry.",
        token_exchange_failed: "Google didn't accept the connection — please retry.",
        access_denied: "You cancelled the Gmail connect flow.",
      };
      toast.error(map[err] || `Gmail connect failed: ${err}`);
      url.searchParams.delete("gmail_error");
      touched = true;
      setTab("setup");
    }
    if (touched) {
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-ink-50" data-testid="outreach-settings-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-900 mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <header className="mb-8">
          <div className="inline-grid place-items-center w-10 h-10 rounded-sm bg-ink-900 text-white mb-3">
            <Mail className="w-5 h-5" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-ink-900">
            AI Outreach
          </h1>
          <p className="text-ink-500 mt-2 max-w-2xl">
            Connect your email provider. We'll draft personalized cold emails from your enriched
            lead data and send them through your inbox — so all replies, deliverability, and
            tracking stay yours.
          </p>
        </header>

        {/* Tab nav */}
        <div className="border-b border-ink-200 mb-8" data-testid="outreach-tabs">
          <div className="flex gap-1">
            {[
              { key: "dashboard",   label: "Dashboard",   icon: LayoutDashboard },
              { key: "inbox",       label: "Inbox",       icon: Inbox },
              { key: "sequences",   label: "Sequences",   icon: Workflow },
              { key: "activity",    label: "Activity",    icon: ActivityIcon },
              { key: "setup",       label: "Setup",       icon: SettingsIcon },
              { key: "suppression", label: "Suppressed",  icon: Ban },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`px-4 py-3 text-xs uppercase tracking-widestx font-semibold inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                    tab === t.key
                      ? "border-ink-900 text-ink-900"
                      : "border-transparent text-ink-400 hover:text-ink-700"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {tab === "dashboard" && (
          <div data-testid="outreach-dashboard">
            {config && !config.imap_host && (
              <div className="bg-[#FFF8E5] border border-[#F0C36D] rounded-sm p-4 mb-6 flex items-start gap-3" data-testid="imap-missing-warning">
                <AlertTriangle className="w-5 h-5 text-[#B27A00] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-ink-900 text-sm">Reply tracking is OFF</div>
                  <p className="text-ink-700 text-[13px] leading-relaxed mt-1">
                    Add IMAP credentials in Setup so we can scan your inbox and detect when prospects reply.
                    Without IMAP, the open and click pixel still works, but replies will show as <strong>0</strong>.
                  </p>
                </div>
                <Button
                  onClick={() => setTab("setup")}
                  variant="outline"
                  className="rounded-sm h-9"
                  data-testid="dashboard-fix-imap"
                >
                  Connect IMAP
                </Button>
              </div>
            )}
            <OutreachStats showEmpty />
            <div className="bg-white border border-ink-100 rounded-sm p-6 grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="dashboard-summary">
              <div>
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">
                  Email credits
                </div>
                <div className="font-display text-3xl font-extrabold text-ink-900">
                  {balance.balance.toLocaleString()}
                </div>
                <div className="text-[11px] font-mono text-ink-400 mt-1">
                  {balance.purchased.toLocaleString()} purchased · {balance.used.toLocaleString()} used
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">
                  SMTP / IMAP status
                </div>
                <div className={`font-display text-xl font-extrabold ${config ? "text-[#1B5E20]" : "text-ink-700"}`}>
                  {config ? (config.imap_host ? "Connected" : "SMTP only") : "Not connected"}
                </div>
                <div className="text-[11px] font-mono text-ink-400 mt-1 truncate">
                  {config?.username || "Connect in the Setup tab"}
                </div>
              </div>
              <div className="flex items-end justify-end gap-2">
                {config?.imap_host && (
                  <SyncRepliesButton />
                )}
                <Button
                  onClick={() => setTab("setup")}
                  variant="outline"
                  className="rounded-sm h-9"
                  data-testid="dashboard-goto-setup"
                >
                  {config ? "Manage setup" : "Connect SMTP"}
                </Button>
              </div>
            </div>
          </div>
        )}
        {tab === "activity" && <OutreachActivity />}
        {tab === "inbox" && <OutreachInbox />}
        {tab === "suppression" && <SuppressionList />}
        {tab === "sequences" && <SequencesPanel />}
        {tab === "setup" && (
        <>
        {/* Credit balance card */}
        <div className="bg-white border border-ink-100 rounded-sm p-6 mb-8" data-testid="email-credit-balance">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="grid place-items-center w-9 h-9 rounded-sm bg-[#FFF4ED] text-brand">
                <Coins className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                  Email credits
                </div>
                <div className="font-display text-3xl font-extrabold text-ink-900">
                  {balance.balance.toLocaleString()}
                </div>
              </div>
            </div>
            <div className="text-right text-[11px] font-mono text-ink-400">
              <div>Purchased {balance.purchased.toLocaleString()}</div>
              <div>Used {balance.used.toLocaleString()}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {packs.map((p) => (
              <button
                key={p.id}
                onClick={() => buyPack(p)}
                disabled={packBusy === p.id}
                data-testid={`buy-pack-${p.id}`}
                className="text-left border border-ink-100 hover:border-ink-900 rounded-sm p-4 transition-colors disabled:opacity-50 group"
              >
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">
                  {p.label.split("—")[0].trim()}
                </div>
                <div className="font-display font-extrabold text-2xl text-ink-900 mb-1">
                  ${p.price_usd}
                </div>
                <div className="text-xs font-mono text-ink-500">
                  {p.credits.toLocaleString()} credits · ${(p.price_usd / p.credits).toFixed(3)}/email
                </div>
                {packBusy === p.id && <Loader2 className="w-3 h-3 animate-spin mt-2 text-ink-900" />}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-ink-400 mt-4 leading-relaxed">
            1 credit = 1 AI-drafted email. Sending, tracking opens, and replies are free.
            Credits never expire.
          </p>
        </div>

        {/* Gmail one-click connect (OAuth · gmail.send) */}
        <div className="bg-white border border-ink-100 rounded-sm p-6 mb-4">
            <div className="flex items-start gap-4">
              <div className="grid place-items-center w-9 h-9 rounded-sm bg-ink-100 shrink-0">
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                  Gmail · one-click
                </div>
                <div className="font-display font-extrabold text-lg text-ink-900 mb-1">
                  {isGmailOAuth ? `Connected · ${config.username || ""}` : "Connect Gmail directly"}
                </div>
                <p className="text-sm text-ink-500 mb-4 max-w-xl">
                  {isGmailOAuth
                    ? "Outreach will send through your Gmail account via the Gmail API. No app password needed. Revoke anytime here or at myaccount.google.com."
                    : "Skip the app-password dance — one click and outreach sends through your Gmail via the Gmail API. Only the 'send email' permission is requested; we can't read your inbox."}
                </p>
                {isGmailOAuth ? (
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm bg-[#E8F5E9] text-[#1B5E20]">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                    <Button
                      variant="outline"
                      onClick={disconnectGmail}
                      data-testid="gmail-disconnect-button"
                      className="h-9 rounded-sm border-ink-200 text-ink-700 hover:border-red-400 hover:text-red-600"
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={connectGmail}
                    data-testid="gmail-connect-button"
                    className="h-10 rounded-sm bg-ink-900 hover:bg-ink-800 text-white font-medium"
                  >
                    Connect Gmail
                  </Button>
                )}
              </div>
            </div>
          </div>

        {!isGmailOAuth && (
        <div className="mb-2">
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2">
            …or configure a custom SMTP server
          </div>
        </div>
        )}
        {/* SMTP setup */}
        {!isGmailOAuth && (
        <div className="bg-white border border-ink-100 rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="grid place-items-center w-9 h-9 rounded-sm bg-ink-100 text-ink-700">
                <Server className="w-4 h-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                  SMTP / Outgoing mail
                </div>
                <div className="font-display font-extrabold text-lg text-ink-900">
                  {config ? "Connected" : "Not connected"}
                </div>
              </div>
            </div>
            {config?.verified_at ? (
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm bg-[#E8F5E9] text-[#1B5E20]">
                <CheckCircle2 className="w-3 h-3" /> Verified
              </span>
            ) : config ? (
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm bg-[#FFF8E1] text-[#F57F17]">
                <AlertTriangle className="w-3 h-3" /> Untested
              </span>
            ) : null}
          </div>

          {/* Preset shortcuts */}
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2">
              Quick setup
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p)}
                  className="text-xs font-medium px-3 py-1.5 border border-ink-200 hover:border-ink-900 hover:text-ink-900 text-ink-600 rounded-sm transition-colors"
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={save} className="space-y-4" data-testid="smtp-form">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  SMTP host
                </Label>
                <Input
                  data-testid="smtp-host-input"
                  type="text"
                  value={form.host}
                  onChange={(e) => setForm({ ...form, host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  className="mt-2 rounded-sm h-11 font-mono text-sm"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  Port
                </Label>
                <Input
                  data-testid="smtp-port-input"
                  type="number"
                  value={form.port}
                  onChange={(e) => setForm({ ...form, port: e.target.value })}
                  className="mt-2 rounded-sm h-11 font-mono text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  Username (email)
                </Label>
                <Input
                  data-testid="smtp-username-input"
                  type="email"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="you@company.com"
                  className="mt-2 rounded-sm h-11 font-mono text-sm"
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                    Password / App password
                  </Label>
                  {(() => {
                    const matched = detectPresetByHost(form.host);
                    const help = matched || {
                      help_url: "https://support.google.com/accounts/answer/185833",
                      help_label: "How to get an App password",
                    };
                    return (
                      <a
                        href={help.help_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="smtp-password-help-link"
                        className="text-[10px] uppercase tracking-widestx font-semibold text-brand hover:text-brand-hover inline-flex items-center gap-1"
                      >
                        {help.help_label}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    );
                  })()}
                </div>
                <Input
                  data-testid="smtp-password-input"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={config?.password_set ? "•••••••• (leave empty to keep)" : "App password"}
                  className="mt-2 rounded-sm h-11 font-mono text-sm"
                />
                <p className="mt-1.5 text-[11px] text-ink-400 leading-relaxed">
                  Most providers (Gmail, Outlook, Zoho, Yahoo) require a 16-character{" "}
                  <span className="font-semibold text-ink-700">App Password</span>, not your normal
                  account password. Pick a preset above and follow the link for step-by-step setup.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  From name
                </Label>
                <Input
                  data-testid="smtp-from-name-input"
                  type="text"
                  value={form.from_name}
                  onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                  placeholder="Alex Chen"
                  className="mt-2 rounded-sm h-11"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  From address
                </Label>
                <Input
                  data-testid="smtp-from-address-input"
                  type="email"
                  value={form.from_address}
                  onChange={(e) => setForm({ ...form, from_address: e.target.value })}
                  placeholder="alex@company.com"
                  className="mt-2 rounded-sm h-11 font-mono text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <input
                id="use_ssl"
                data-testid="smtp-ssl-toggle"
                type="checkbox"
                checked={form.use_ssl}
                onChange={(e) => setForm({ ...form, use_ssl: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="use_ssl" className="text-sm text-ink-700">
                Use SSL (port 465). Uncheck for STARTTLS (587).
              </label>
            </div>

            {/* IMAP — for inbound reply tracking */}
            <div className="pt-5 mt-2 border-t border-ink-100">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                    IMAP · for reply tracking
                  </div>
                  <div className="text-[11px] text-ink-500 mt-0.5">
                    Optional. Lets us auto-detect replies to your sent emails and surface them in
                    the Inbox tab. Same username/password as SMTP.
                  </div>
                </div>
                {config?.imap_verified_at && (
                  <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm bg-[#E8F5E9] text-[#1B5E20]">
                    <CheckCircle2 className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                    IMAP host
                  </Label>
                  <Input
                    data-testid="imap-host-input"
                    type="text"
                    value={form.imap_host}
                    onChange={(e) => setForm({ ...form, imap_host: e.target.value })}
                    placeholder="imap.gmail.com"
                    className="mt-2 rounded-sm h-11 font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                    IMAP port
                  </Label>
                  <Input
                    data-testid="imap-port-input"
                    type="number"
                    value={form.imap_port}
                    onChange={(e) => setForm({ ...form, imap_port: e.target.value })}
                    className="mt-2 rounded-sm h-11 font-mono text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <input
                  id="imap_ssl"
                  data-testid="imap-ssl-toggle"
                  type="checkbox"
                  checked={form.imap_use_ssl}
                  onChange={(e) => setForm({ ...form, imap_use_ssl: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="imap_ssl" className="text-sm text-ink-700">
                  Use IMAPS (port 993). Uncheck for plain (143 + STARTTLS).
                </label>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-ink-100">
              <Button
                type="submit"
                disabled={busy}
                className="h-10 px-4 rounded-sm bg-ink-900 hover:bg-ink-800 text-white font-medium tracking-wide"
                data-testid="smtp-save-button"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save credentials"}
              </Button>
              {config && (
                <>
                  <Button
                    type="button"
                    onClick={test}
                    disabled={testing}
                    variant="outline"
                    className="h-10 px-4 rounded-sm border-ink-200 hover:border-ink-900 text-ink-700"
                    data-testid="smtp-test-button"
                  >
                    {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><ShieldCheck className="w-4 h-4 mr-2" /> Test SMTP</>)}
                  </Button>
                  {config.imap_host && (
                    <Button
                      type="button"
                      onClick={testImap}
                      disabled={testingImap}
                      variant="outline"
                      className="h-10 px-4 rounded-sm border-ink-200 hover:border-ink-900 text-ink-700"
                      data-testid="imap-test-button"
                    >
                      {testingImap ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Inbox className="w-4 h-4 mr-2" /> Test IMAP</>)}
                    </Button>
                  )}
                  <button
                    type="button"
                    onClick={remove}
                    className="text-[10px] uppercase tracking-widestx font-semibold text-ink-400 hover:text-red-600 inline-flex items-center gap-1 ml-auto"
                    data-testid="smtp-remove-button"
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </>
              )}
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-ink-100 text-[11px] text-ink-500 leading-relaxed">
            <strong className="text-ink-700">Security:</strong> your SMTP password is encrypted with
            AES-128 (Fernet) before being stored. It's never shown back to you, never logged, and
            never sent to any third party.
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1.5">
                App-password setup guides
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {PRESETS.map((p) => (
                  <a
                    key={p.key}
                    href={p.help_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`help-link-${p.key}`}
                    className="text-brand hover:underline inline-flex items-center gap-1"
                  >
                    {p.label} <ExternalLink className="w-3 h-3" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Sequence cooldown setting */}
        <CooldownSetting />

        {/* Sent emails preview */}
        <SentEmailsPreview />
        </>
        )}
      </div>
    </div>
  );
}

function CooldownSetting() {
  const [days, setDays] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http
      .get("/outreach/cooldown")
      .then((r) => {
        setDays(r.data.cooldown_days);
        setDraft(String(r.data.cooldown_days));
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    const n = Math.max(0, Math.min(365, parseInt(draft, 10) || 0));
    setBusy(true);
    try {
      await http.post("/outreach/cooldown", { cooldown_days: n });
      setDays(n);
      setDraft(String(n));
      toast.success(
        n === 0
          ? "Cooldown disabled — leads can be re-enrolled immediately."
          : `Cooldown set to ${n} day${n === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  if (days === null) return null;

  return (
    <div className="bg-white border border-ink-100 rounded-sm p-6 mt-6" data-testid="cooldown-setting">
      <div className="flex items-center gap-2 mb-2">
        <div className="font-display text-base font-bold text-ink-900">Sequence cooldown</div>
        <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 bg-ink-50 px-1.5 py-0.5 rounded-sm">
          sales hygiene
        </span>
      </div>
      <p className="text-ink-500 text-sm leading-relaxed mb-4">
        After a lead exits a sequence (replied, stopped, or completed), we block
        re-enrollment for this many days. Default 30. Set to <strong>0</strong> to disable.
      </p>
      <div className="flex items-end gap-3 max-w-md">
        <div className="flex-1">
          <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
            Cooldown window
          </Label>
          <div className="relative mt-1.5">
            <Input
              type="number"
              min="0"
              max="365"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid="cooldown-input"
              className="rounded-sm h-11 font-mono pr-14"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-ink-400">
              days
            </span>
          </div>
        </div>
        <Button
          onClick={save}
          disabled={busy || String(days) === draft}
          data-testid="cooldown-save"
          className="rounded-sm h-11 bg-ink-900 hover:bg-ink-800 text-white"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}



function SyncRepliesButton() {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/outreach/imap/sync");
      if (data.new_replies > 0) {
        toast.success(
          `Synced — ${data.new_replies} new repl${data.new_replies === 1 ? "y" : "ies"} captured (${data.polled_messages} message${data.polled_messages === 1 ? "" : "s"} scanned)`,
        );
      } else {
        toast.info(
          `Synced — no new replies (${data.polled_messages} message${data.polled_messages === 1 ? "" : "s"} scanned)`,
        );
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Sync failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      onClick={onClick}
      disabled={busy}
      variant="outline"
      className="rounded-sm h-9"
      data-testid="dashboard-sync-replies"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />}
      Sync replies
    </Button>
  );
}

function SuppressionList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/outreach/suppression");
      setItems(data.items || []);
    } catch (err) {
      console.warn("Failed to load suppression list:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!adding) return;
    try {
      await http.post("/outreach/suppression", { email: adding.toLowerCase(), reason: "manual" });
      setAdding("");
      toast.success("Added to suppression list");
      load();
    } catch (err) {
      toast.error("Could not add");
    }
  };
  const remove = async (em) => {
    try {
      await http.delete(`/outreach/suppression/${encodeURIComponent(em)}`);
      load();
    } catch {
      toast.error("Could not remove");
    }
  };
  return (
    <div data-testid="suppression-tab">
      <div className="bg-white border border-ink-100 rounded-sm p-6 mb-4">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-3">
          Add a manual suppression
        </div>
        <form onSubmit={add} className="flex gap-2">
          <Input
            type="email"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            placeholder="someone@company.com"
            className="rounded-sm h-10 font-mono text-sm"
            data-testid="suppression-add-input"
          />
          <Button type="submit" className="h-10 rounded-sm bg-ink-900 text-white" data-testid="suppression-add-button">
            Suppress
          </Button>
        </form>
        <p className="text-[11px] text-ink-400 mt-3 leading-relaxed">
          Anyone on this list will never receive emails from you via LeadIQ Pro. People who click
          your unsubscribe links are added automatically.
        </p>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-ink-500">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-sm text-ink-500">No suppressed addresses yet.</div>
      ) : (
        <div className="bg-white border border-ink-100 rounded-sm divide-y divide-ink-100">
          {items.map((it) => (
            <div key={it.id} className="px-5 py-3 flex items-center justify-between text-sm">
              <div>
                <div className="font-mono text-ink-900">{it.email}</div>
                <div className="text-[10px] font-mono text-ink-400">
                  {it.reason} · {new Date(it.added_at).toLocaleString()}
                </div>
              </div>
              <button
                onClick={() => remove(it.email)}
                className="text-[10px] uppercase tracking-widestx font-semibold text-ink-400 hover:text-red-600"
                data-testid={`suppression-remove-${it.email}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SentEmailsPreview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    http
      .get("/outreach/sent")
      .then(({ data }) => alive && setItems((data.emails || []).slice(0, 10)))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);
  if (loading) return null;
  if (!items.length) return null;
  return (
    <div className="mt-8 bg-white border border-ink-100 rounded-sm" data-testid="sent-emails-list">
      <div className="px-6 py-4 border-b border-ink-100 flex items-center gap-2">
        <Send className="w-4 h-4 text-ink-400" />
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
          Recent activity
        </div>
      </div>
      <div className="divide-y divide-ink-100">
        {items.map((e) => (
          <div key={e.id} className="px-6 py-3 flex items-center justify-between text-sm gap-4" data-testid={`sent-${e.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-ink-900 truncate">{e.subject || "(no subject)"}</div>
              <div className="text-[11px] font-mono text-ink-400">{e.to || "draft"} · {new Date(e.sent_at || e.created_at).toLocaleString()}</div>
            </div>
            {/* Per-row engagement badges (only for sent emails) */}
            {e.status === "sent" && (
              <div className="hidden sm:flex items-center gap-3 text-[11px] font-mono text-ink-500 shrink-0" data-testid={`sent-stats-${e.id}`}>
                <Stat icon="eye" count={e.open_count || 0} fired={!!e.first_open_at} title="opens" />
                <Stat icon="click" count={e.click_count || 0} fired={!!e.first_click_at} title="clicks" />
                <Stat icon="reply" count={e.reply_count || 0} fired={!!e.replied_at} title="replies" />
              </div>
            )}
            <span
              className={`text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm shrink-0 ${
                e.status === "sent"
                  ? "bg-[#E8F5E9] text-[#1B5E20]"
                  : e.status === "failed"
                  ? "bg-[#FFEBEE] text-[#C62828]"
                  : "bg-ink-100 text-ink-600"
              }`}
            >
              {e.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ icon, count, fired, title }) {
  // Color when ≥1; muted otherwise.
  const colorClass = !fired
    ? "text-ink-300"
    : icon === "eye"
    ? "text-[#0D47A1]"
    : icon === "click"
    ? "text-[#FF6B35]"
    : "text-[#1B5E20]";
  const Icon = icon === "eye" ? Eye : icon === "click" ? MousePointerClick : MessageCircle;
  return (
    <span className={`inline-flex items-center gap-1 ${colorClass}`} title={`${count} ${title}`}>
      <Icon className="w-3 h-3" />
      <span className="tabular-nums">{count}</span>
    </span>
  );
}
